import React, { useState, useCallback, useMemo } from "react";
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  Button,
  Text,
  Box,
  Input,
  InputGroup,
  InputRightElement,
  IconButton,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Progress,
  useToast,
  VStack,
  HStack,
  Icon,
} from "@chakra-ui/react";
import { AiOutlineEye, AiOutlineEyeInvisible } from "react-icons/ai";
import { FiLock, FiCheck, FiX } from "react-icons/fi";
import { useDispatch, useSelector } from "react-redux";
import { changePasswordAPI } from "../service/AuthService";
import { buttonColors, Encrypt } from "../utils/constants";

const PASSWORD_REGEX =
  /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/;

const PASSWORD_RULES = [
  { label: "At least 8 characters", test: (p) => p.length >= 8 },
  { label: "One uppercase letter", test: (p) => /[A-Z]/.test(p) },
  { label: "One lowercase letter", test: (p) => /[a-z]/.test(p) },
  { label: "One number", test: (p) => /\d/.test(p) },
  { label: "One special character (@$!%*?&)", test: (p) => /[@$!%*?&]/.test(p) },
];

function getStrength(password) {
  if (!password) return 0;
  const passed = PASSWORD_RULES.filter((r) => r.test(password)).length;
  return Math.round((passed / PASSWORD_RULES.length) * 100);
}

function getStrengthColor(strength) {
  if (strength <= 20) return "red";
  if (strength <= 40) return "orange";
  if (strength <= 60) return "yellow";
  if (strength <= 80) return "blue";
  return "green";
}

function getStrengthLabel(strength) {
  if (strength <= 20) return "Very Weak";
  if (strength <= 40) return "Weak";
  if (strength <= 60) return "Fair";
  if (strength <= 80) return "Good";
  return "Strong";
}

export default function ChangePasswordModal() {
  const dispatch = useDispatch();
  const toast = useToast();

  const userData = useSelector((state) => state.account.userData);
  const isOpen = useSelector(
    (state) => state.account.ui.changePasswordPopOpen
  );
  const isForced = Boolean(userData?.token && userData?.resetRequired);

  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showOld, setShowOld] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState({});

  const strength = useMemo(() => getStrength(newPassword), [newPassword]);

  const errors = useMemo(() => {
    const errs = {};
    if (touched.oldPassword && !oldPassword) {
      errs.oldPassword = "Old password is required";
    }
    if (touched.newPassword && !newPassword) {
      errs.newPassword = "New password is required";
    } else if (touched.newPassword && !PASSWORD_REGEX.test(newPassword)) {
      errs.newPassword = "Password doesn't meet requirements";
    } else if (touched.newPassword && newPassword === oldPassword && oldPassword) {
      errs.newPassword = "New password must differ from old password";
    }
    if (touched.confirmPassword && !confirmPassword) {
      errs.confirmPassword = "Please confirm your password";
    } else if (touched.confirmPassword && newPassword !== confirmPassword) {
      errs.confirmPassword = "Passwords do not match";
    }
    return errs;
  }, [oldPassword, newPassword, confirmPassword, touched]);

  const isFormValid = useMemo(() => {
    return (
      oldPassword &&
      newPassword &&
      confirmPassword &&
      PASSWORD_REGEX.test(newPassword) &&
      newPassword !== oldPassword &&
      newPassword === confirmPassword
    );
  }, [oldPassword, newPassword, confirmPassword]);

  const resetForm = useCallback(() => {
    setOldPassword("");
    setNewPassword("");
    setConfirmPassword("");
    setShowOld(false);
    setShowNew(false);
    setShowConfirm(false);
    setTouched({});
    setIsLoading(false);
  }, []);

  const logoutAndRedirect = useCallback(() => {
    dispatch({ type: "accountData/logOut", payload: {} });
    dispatch({ type: "gameData/logOut", payload: {} });
    setTimeout(() => {
      localStorage.clear();
      window.location.href = "/";
    }, 300);
  }, [dispatch]);

  const handleClose = useCallback(() => {
    resetForm();
    if (isForced) {
      logoutAndRedirect();
    }
    dispatch({ type: "accountData/setChangePopSate", payload: false });
  }, [isForced, resetForm, logoutAndRedirect, dispatch]);

  const handleSubmit = useCallback(async () => {
    setTouched({ oldPassword: true, newPassword: true, confirmPassword: true });

    if (!isFormValid) return;

    setIsLoading(true);
    try {
      const res = await changePasswordAPI({
        newPassword: Encrypt(newPassword),
        password: Encrypt(oldPassword),
        resetRequired: false,
      });

      // Use loose equality to handle both numeric 200 and string "200"
      if (res?.code == 200 || res?.statusCode == 200) {
        toast({
          title: "Password Changed",
          description: "Please login with your new password.",
          status: "success",
          duration: 3000,
          isClosable: true,
          position: "top",
        });
        resetForm();
        dispatch({ type: "accountData/setChangePopSate", payload: false });
        logoutAndRedirect();
        return;
      }

      // Ensure message is always a string
      const rawMsg =
        res?.message || res?.response || res?.error || "Old password is incorrect";
      const message = typeof rawMsg === "string" ? rawMsg : "Old password is incorrect";
      toast({
        description: message,
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } catch {
      toast({
        description: "Something went wrong. Please try again.",
        status: "error",
        duration: 4000,
        isClosable: true,
        position: "top",
      });
    } finally {
      setIsLoading(false);
    }
  }, [
    isFormValid,
    oldPassword,
    newPassword,
    toast,
    resetForm,
    logoutAndRedirect,
    dispatch,
  ]);

  const handleBlur = (field) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  return (
    <Modal
      size="md"
      isOpen={isOpen}
      onClose={handleClose}
      closeOnOverlayClick={!isForced}
      closeOnEsc={!isForced}
      motionPreset="slideInBottom"
      isCentered
    >
      <ModalOverlay bg="blackAlpha.600" backdropFilter="blur(4px)" />
      <ModalContent
        className="change-pwd-modal"
        mx={{ base: "3", md: "4" }}
        maxW={{ base: "95vw", sm: "400px", md: "440px" }}
        borderRadius="12px"
        overflow="hidden"
        bg="var(--modal-bg, #fff)"
        boxShadow="0 20px 60px rgba(0,0,0,0.3)"
      >
        {/* Header */}
        <ModalHeader
          className="change-pwd-modal__header"
          bg="var(--header-bg, #2c3e50)"
          py={{ base: "2.5", md: "3" }}
          px={{ base: "3", md: "4" }}
          display="flex"
          alignItems="center"
          gap="2"
        >
          <Icon as={FiLock} color="#fff" boxSize="4" />
          <Text fontSize={{ base: "sm", md: "md" }} fontWeight="600" color="#fff">
            {isForced ? "Set New Password" : "Change Password"}
          </Text>
        </ModalHeader>
        {!isForced && <ModalCloseButton color="#fff" top="2" />}

        <ModalBody className="change-pwd-modal__body" px={{ base: "3", md: "5" }} py={{ base: "4", md: "5" }}>
          <VStack spacing="4" align="stretch">
            {isForced && (
              <Box
                bg="var(--theme1-bg, #f0f4ff)"
                borderRadius="8px"
                p="3"
                borderLeft="3px solid"
                borderLeftColor={buttonColors}
              >
                <Text fontSize="xs" color="var(--text-body, #555)">
                  For security, please set a new password before continuing.
                </Text>
              </Box>
            )}

            {/* Old Password */}
            <FormControl isInvalid={!!errors.oldPassword}>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color="var(--text-body, #333)"
                mb="1"
              >
                Old Password
              </FormLabel>
              <InputGroup size="sm">
                <Input
                  type={showOld ? "text" : "password"}
                  value={oldPassword}
                  onChange={(e) => setOldPassword(e.target.value)}
                  onBlur={() => handleBlur("oldPassword")}
                  placeholder="Enter old password"
                  bg="var(--bg-input, #f5f5f5)"
                  borderColor="var(--border-color, #ddd)"
                  borderRadius="6px"
                  _focus={{
                    borderColor: buttonColors,
                    boxShadow: `0 0 0 1px ${buttonColors}`,
                  }}
                  pr="2.5rem"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showOld ? "Hide password" : "Show password"}
                    icon={showOld ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                    size="xs"
                    variant="ghost"
                    color="var(--text-body, #666)"
                    onClick={() => setShowOld(!showOld)}
                    tabIndex={-1}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage fontSize="xs">{errors.oldPassword}</FormErrorMessage>
            </FormControl>

            {/* New Password */}
            <FormControl isInvalid={!!errors.newPassword}>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color="var(--text-body, #333)"
                mb="1"
              >
                New Password
              </FormLabel>
              <InputGroup size="sm">
                <Input
                  type={showNew ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  onBlur={() => handleBlur("newPassword")}
                  placeholder="Enter new password"
                  bg="var(--bg-input, #f5f5f5)"
                  borderColor="var(--border-color, #ddd)"
                  borderRadius="6px"
                  _focus={{
                    borderColor: buttonColors,
                    boxShadow: `0 0 0 1px ${buttonColors}`,
                  }}
                  pr="2.5rem"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showNew ? "Hide password" : "Show password"}
                    icon={showNew ? <AiOutlineEyeInvisible /> : <AiOutlineEye />}
                    size="xs"
                    variant="ghost"
                    color="var(--text-body, #666)"
                    onClick={() => setShowNew(!showNew)}
                    tabIndex={-1}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage fontSize="xs">{errors.newPassword}</FormErrorMessage>

              {/* Strength indicator */}
              {newPassword && (
                <Box mt="2">
                  <HStack justify="space-between" mb="1">
                    <Text fontSize="xs" color="var(--text-body, #888)">
                      Strength
                    </Text>
                    <Text
                      fontSize="xs"
                      fontWeight="600"
                      color={`${getStrengthColor(strength)}.500`}
                    >
                      {getStrengthLabel(strength)}
                    </Text>
                  </HStack>
                  <Progress
                    value={strength}
                    size="xs"
                    borderRadius="full"
                    colorScheme={getStrengthColor(strength)}
                  />
                  <VStack align="stretch" spacing="1" mt="2">
                    {PASSWORD_RULES.map((rule) => {
                      const passed = rule.test(newPassword);
                      return (
                        <HStack key={rule.label} spacing="1.5">
                          <Icon
                            as={passed ? FiCheck : FiX}
                            boxSize="3"
                            color={passed ? "green.500" : "red.400"}
                          />
                          <Text
                            fontSize="xs"
                            color={
                              passed
                                ? "green.600"
                                : "var(--text-body, #888)"
                            }
                          >
                            {rule.label}
                          </Text>
                        </HStack>
                      );
                    })}
                  </VStack>
                </Box>
              )}
            </FormControl>

            {/* Confirm Password */}
            <FormControl isInvalid={!!errors.confirmPassword}>
              <FormLabel
                fontSize="sm"
                fontWeight="500"
                color="var(--text-body, #333)"
                mb="1"
              >
                Confirm Password
              </FormLabel>
              <InputGroup size="sm">
                <Input
                  type={showConfirm ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  onBlur={() => handleBlur("confirmPassword")}
                  placeholder="Confirm new password"
                  bg="var(--bg-input, #f5f5f5)"
                  borderColor="var(--border-color, #ddd)"
                  borderRadius="6px"
                  _focus={{
                    borderColor: buttonColors,
                    boxShadow: `0 0 0 1px ${buttonColors}`,
                  }}
                  pr="2.5rem"
                />
                <InputRightElement>
                  <IconButton
                    aria-label={showConfirm ? "Hide password" : "Show password"}
                    icon={
                      showConfirm ? <AiOutlineEyeInvisible /> : <AiOutlineEye />
                    }
                    size="xs"
                    variant="ghost"
                    color="var(--text-body, #666)"
                    onClick={() => setShowConfirm(!showConfirm)}
                    tabIndex={-1}
                  />
                </InputRightElement>
              </InputGroup>
              <FormErrorMessage fontSize="xs">
                {errors.confirmPassword}
              </FormErrorMessage>
            </FormControl>

            {/* Submit */}
            <Button
              onClick={handleSubmit}
              isLoading={isLoading}
              loadingText="Changing Password..."
              isDisabled={!isFormValid || isLoading}
              w="100%"
              bg={buttonColors}
              color="#fff"
              size="sm"
              borderRadius="6px"
              fontWeight="600"
              mt="1"
              _hover={{ opacity: 0.9 }}
              _active={{ opacity: 0.8 }}
              _disabled={{ opacity: 0.5, cursor: "not-allowed" }}
            >
              {isForced ? "Set New Password" : "Change Password"}
            </Button>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
